import { NULL_REPORT_ID } from 'constants/report';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import JobParamsQuery, {
  JobParamsResponse,
} from '@graphql/queries/JobParamsQuery';
import OptimizationReportsByParentJobIdQuery, {
  OptimizationReportsByParentJobIdResponse,
} from '@graphql/queries/OptReportsByParentJobIdQuery';
import {
  Checkbox, Container, Grid, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Field } from 'redux-form';
import { formatDate } from 'utils/dateTime';

import VersionPicker from './VersionPicker';
import FormInput from '../../../components/FormInput';
import FormInputSelect from '../../../components/FormInputSelect';
import { arrayify } from '../../../utils/afsUtils';
import styles from '../RequestReport.module.css';
import SelectReport from '../SelectReport';
import { generateArtifactPath } from '../SelectReport/SelectReport';

export function optTargetOptions(selectedJobParams) {
  return arrayify(
    selectedJobParams?.reportJobByReportId?.params?.competitive_set_products ??
    [],
  ).map((p) => ({ label: p, value: p }));
}
export interface ReportJobData {
  jobId: string;
  startedAt: string;
  reportType: string;
  reportId: string;
  targetGroupName: string;
  clientName: string;
  projectName: string;
  workspaceId: string;
  artifact_path: string;
}

interface Props {
  change: (field: string, value: any) => void;
  displayMode: boolean;
  optimization: any;
  templateReportId: string;
}

//AIPD-885 mix and match component
// eslint-disable-next-line complexity -- pre-existing complexity (26); surfaced by the ESLint 9 flat-config migration (AIPD-1397), refactor tracked separately
const MixAndMatch: React.FC<Props> = (props: Props) => {
  const { change, displayMode } = props;
  const { t } = useTranslation();

  const [selectedReport, setSelectedReport] = useState<ReportJobData>(
    props.optimization?.selected_report ?? null,
  );
  const [selectedOptReports, setSelectedOptReports] = useState<Set<string>>(
    new Set(props.optimization?.selected_opt_report_ids ?? []),
  );
  const [versionPick, setVersionPick] = useState<Record<string, string[]>>(() => {
    const entries = (props.optimization?.mix_and_match ?? []) as Array<{ report_id: string; versions: string[] }>;
    const record: Record<string, string[]> = {};
    for (const entry of entries) {
      if (entry?.report_id) {
        record[entry.report_id] = entry.versions ?? [];
      }
    }
    return record;
  });

  const [selectedProduct, setSelectedProduct] = useState<string | null>(
    props.optimization?.products_to_opt?.value ?? null,
  );

  const handleVersionChange = (reportId: string, versions: string[]) => {
    setVersionPick((prev) => ({ ...prev, [reportId]: versions }));
  };

  const toggleOptReport = (reportId: string) => {
    setSelectedOptReports((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) {
        next.delete(reportId);
      } else {
        next.add(reportId);
      }
      return next;
    });
  };

  const resetFields = () => {
    change('optimization.products_to_opt', []);
    setSelectedOptReports(new Set());
    setSelectedProduct(null);
  };

  const {
    data: selectedJobParams,
    loading: jobParamsLoading,
    error,
  } = useQuery<JobParamsResponse>(JobParamsQuery, {
    variables: {
      reportId:
        selectedReport?.reportId ?? props.templateReportId ?? NULL_REPORT_ID,
    },
  });

  const { data: optimizationReports, loading: optReportsLoading } =
    useQuery<OptimizationReportsByParentJobIdResponse>(
      OptimizationReportsByParentJobIdQuery,
      {
        variables: {
          parentJobId: selectedReport?.jobId,
          orderBy: 'STARTED_AT_DESC',
        },
        skip: !selectedReport,
      },
    );


  useEffect(() => {
    if (selectedReport && selectedJobParams) {
      change(
        'optimization.artifact_path',
        generateArtifactPath(
          selectedReport,
          selectedJobParams?.reportJobByReportId?.params['project_name'],
        ),
      );
      change('optimization.countries', selectedJobParams?.reportJobByReportId?.params['countries']);
      change('optimization.age_ranges', selectedJobParams?.reportJobByReportId?.params['age_ranges']);
      change('optimization.include_male', selectedJobParams?.reportJobByReportId?.params['include_male']);
      change('optimization.include_female', selectedJobParams?.reportJobByReportId?.params['include_female']);
      change('optimization.SansRejector', selectedJobParams?.reportJobByReportId?.params['SansRejector']);
      change('optimization.custom_class_db_name', selectedJobParams?.reportJobByReportId?.params['custom_class_db_name']);
      change('optimization.competitive_set_folders', selectedJobParams?.reportJobByReportId?.params['competitive_set_folders']);
      change(
        'optimization.competitive_set_products',
        arrayify(
          selectedJobParams?.reportJobByReportId?.params['competitive_set_products'] ??
          [],
        ),
      );
    }
  }, [selectedReport, selectedJobParams]);

  useEffect(() => {
    if (displayMode) return;
    change(
      'optimization.selected_opt_report_ids',
      Array.from(selectedOptReports),
    );
  }, [selectedOptReports, displayMode]);

  useEffect(() => {
    if (displayMode) return;
    const optReportNodes = optimizationReports?.allReportJobs?.nodes ?? [];
    const mixAndMatch = Array.from(selectedOptReports)
      .map((reportId) => {
        const report = optReportNodes.find((r) => r.reportId === reportId);
        return report
          ? {
            report_id: reportId,
            artifact_path: generateArtifactPath(report as any, report.params?.project_name),
            versions: versionPick[reportId] ?? [],
          }
          : null;
      })
      .filter(Boolean);
    change('optimization.mix_and_match', mixAndMatch);
  }, [selectedOptReports, versionPick, optimizationReports, displayMode]);

  return (
    <Container>
      <Grid
        container
        spacing={3}
        style={{ display: 'flex', flexDirection: 'row' }}
      >
        {!props.templateReportId && !displayMode && (
          <div>
            <br />
            <SelectReport
              onlyLinkedOptimizationReports
              initialSelectedReport={selectedReport}
              initialFilterClientName={props.optimization?.filter_client_name}
              initialFilterProjectName={props.optimization?.filter_project_name}
              initialFilterReportId={props.optimization?.filter_report_id}
              initialFilterPassedQA={props.optimization?.filter_passed_qa}
              onFilterChange={(field, value) => change(`optimization.${field}`, value)}
              selectReport={(report: ReportJobData) => {
                change('optimization.workspace_id', report.workspaceId);
                change('optimization.parent_job_id', report.jobId);
                change('optimization.artifact_path', report.artifact_path);
                change('optimization.client_name', report.clientName);
                change('optimization.project_name', report.projectName);
                change(
                  'optimization.target_group_name',
                  report.targetGroupName,
                );
                setSelectedReport(report);
                change('optimization.selected_report', report);
              }}
              resetFields={resetFields}
            />
            <br />
          </div>
        )}
        {!jobParamsLoading &&
          (!!selectedReport || !!props.templateReportId) && (
            <Grid container spacing={1} m={1}>

              <Grid item xs={12}>
                <Field
                  key={`opt_product_picker_report_${selectedJobParams?.reportJobByReportId?.params['reportUID']}`}
                  name={'optimization.products_to_opt'}
                  component={FormInput}
                  inputComponent={FormInputSelect}
                  customLabel
                  isSearchable
                  required
                  disabled={displayMode}
                  hideSelectedOptions={false}
                  labelText={t('reports.request.targetProductForOptimization')}
                  placeholder={t('reports.request.selectProduct')}
                  options={optTargetOptions(selectedJobParams)}
                  onChange={({ value }) => {
                    change('optimization.products_to_opt', value);
                    setSelectedProduct(value);
                    setSelectedOptReports(new Set());
                  }}
                />
              </Grid>
              <br />
              {/* Linked optimization reports table */}
              {!optReportsLoading && selectedProduct && (() => {
                const filteredReports = optimizationReports?.allReportJobs?.nodes?.filter(
                  (report) => report.params?.products_to_opt?.includes(selectedProduct)
                ) ?? [];
                
                if (filteredReports.length === 0) {
                  return <Grid item xs={12}><p>No optimization report was run on <strong>{selectedProduct}</strong> yet.</p></Grid>;
                }
                
                return (
                  <Grid item xs={12}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('reports.request.select')}</TableCell>
                          <TableCell>{t('reports.request.clientName')}</TableCell>
                          <TableCell>{t('reports.request.projectName')}</TableCell>
                          <TableCell>{t('reports.request.targetGroupName')}</TableCell>
                          <TableCell>{t('reports.request.startedAt')}</TableCell>
                          <TableCell>{t('reports.request.reportStatus')}</TableCell>
                          <TableCell>{t('reports.request.passedQa')}</TableCell>
                          <TableCell>{t('reports.request.reportId')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredReports.map(
                            (report) => (
                              <TableRow key={report.reportId}>
                                <TableCell>
                                  <Tooltip title={t('reports.request.selectReportTooltip')} arrow>
                                    <Checkbox
                                      color="secondary"
                                      checked={selectedOptReports.has(
                                        report.reportId,
                                      )}
                                      onChange={() =>
                                        toggleOptReport(report.reportId)
                                      }
                                      disabled={displayMode}
                                    />
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{report.clientName}</TableCell>
                                <TableCell>{report.projectName}</TableCell>
                                <TableCell>{report.targetGroupName}</TableCell>
                                <TableCell>
                                  {formatDate(report.startedAt, 'MMMM d, yyyy h:mm a')}
                                </TableCell>
                                <TableCell>{report.reportStatus}</TableCell>
                                <TableCell>{report.passedQa ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{report.reportId}</TableCell>
                              </TableRow>
                            ),
                          )}
                      </TableBody>
                    </Table>
                    <br />
                  </Grid>
                );
              })()}
              {Array.from(selectedOptReports).length > 0 && (
                <Grid item xs={12}>
                  <Table size="small" className={styles.versionTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.versionTableReportCol}>{t('reports.request.reportId')}</TableCell>
                        <TableCell className={styles.versionTableVersionCol}>{t('reports.request.versions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from(selectedOptReports).map((reportId) => (
                        <TableRow key={`version-${reportId}`}>
                          <TableCell className={styles.versionTableReportCell}>
                            {reportId}
                          </TableCell>
                          <TableCell>
                            <VersionPicker
                              reportId={reportId}
                              onChange={handleVersionChange}
                              disabled={displayMode}
                              value={versionPick[reportId] ?? []}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              )}
            </Grid>
          )}
      </Grid>
    </Container>
  );
};

export default MixAndMatch;